import { SelectField, type SelectFieldProps } from "@/components/design-system/SelectField";
import { portfolioAssetLabel } from "@/portfolio/assetResolution";
import type { PortfolioRow } from "@/portfolio/types";
import { t } from "@lingui/core/macro";
import { useEffect } from "react";

const selectAllValue = "__select-all-assets__";
const aggregateValue = "__aggregate-assets__";

export function PortfolioAssetSelect({
  assets,
  includedAssetIds,
  eligibleAssetIds,
  onChange,
  aggregate = false,
  onAggregateChange,
  showAggregate = true,
}: {
  assets: PortfolioRow[];
  includedAssetIds: string[];
  eligibleAssetIds: string[];
  onChange: (ids: string[]) => void;
  aggregate?: boolean;
  onAggregateChange?: (aggregate: boolean) => void;
  showAggregate?: boolean;
}) {
  const stableIds = new Set([...assets.flatMap((asset) => (asset.id ? [asset.id] : [])), ...includedAssetIds]);
  const valueCounts = new Map<string, number>();
  for (const asset of assets) {
    const value = asset.assetId?.trim();
    if (value) {
      valueCounts.set(value, (valueCounts.get(value) ?? 0) + 1);
    }
  }
  const options = assets.flatMap((asset) => {
    if (!asset.id) {
      return [];
    }
    const assetId = asset.assetId?.trim();
    // Missing/duplicate customer IDs must not merge distinct assets in the dropdown.
    const value =
      assetId && valueCounts.get(assetId) === 1 && (!stableIds.has(assetId) || assetId === asset.id)
        ? assetId
        : asset.id;
    return [
      {
        id: asset.id,
        value,
        label: portfolioAssetLabel(asset),
        disabled: !eligibleAssetIds.includes(asset.id) && !includedAssetIds.includes(asset.id),
      },
    ];
  });
  for (const assetId of includedAssetIds) {
    if (!options.some((option) => option.id === assetId)) {
      options.push({ id: assetId, value: assetId, label: t`Unavailable or invalid asset ${assetId}`, disabled: false });
    }
  }
  const selectableIds = options.filter((option) => eligibleAssetIds.includes(option.id)).map((option) => option.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => includedAssetIds.includes(id));
  const someSelected = !allSelected && selectableIds.some((id) => includedAssetIds.includes(id));
  const selectedValues = options.filter((option) => includedAssetIds.includes(option.id)).map((option) => option.value);
  const selectAllLabel = t`Select all`;
  const aggregateLabel = t`Aggregate`;
  const selectAllDescription = t`Select or clear all eligible assets.`;
  const aggregateDescription = t`Show the combined output of selected assets.`;
  useEffect(() => {
    const setIndeterminateState = () => {
      const specialOptions = Array.from(document.querySelectorAll<HTMLElement>('[role="option"]'));
      const selectAllOption = specialOptions.find((option) => option.textContent?.trim() === selectAllLabel);
      const selectAllCheckbox = selectAllOption?.querySelector<HTMLInputElement>('input[type="checkbox"]');
      if (selectAllOption) {
        selectAllOption.style.fontWeight = "700";
        selectAllOption.title = selectAllDescription;
      }
      if (selectAllCheckbox) {
        selectAllCheckbox.indeterminate = someSelected;
      }
      const aggregateOption = specialOptions.find((option) => option.textContent?.trim() === aggregateLabel);
      if (aggregateOption) {
        aggregateOption.style.fontWeight = "700";
        aggregateOption.title = aggregateDescription;
      }
    };
    const observer = new MutationObserver(setIndeterminateState);
    observer.observe(document.body, { childList: true, subtree: true });
    setIndeterminateState();
    return () => observer.disconnect();
  }, [aggregateDescription, aggregateLabel, selectAllDescription, selectAllLabel, someSelected]);

  const handleChange: NonNullable<SelectFieldProps["onChange"]> = (values) => {
    if (!Array.isArray(values)) {
      return;
    }

    const nextAggregate = showAggregate && values.includes(aggregateValue);
    if (nextAggregate !== aggregate) {
      onAggregateChange?.(nextAggregate);
    }
    const includesSelectAll = values.includes(selectAllValue);
    if (includesSelectAll && !allSelected) {
      onChange(selectableIds);
      return;
    }
    if (
      !includesSelectAll &&
      allSelected &&
      selectableIds.every((id) => values.includes(options.find((option) => option.id === id)?.value ?? ""))
    ) {
      onChange([]);
      return;
    }
    const selectedIds = values.flatMap((value) => {
      const option = options.find((option) => option.value === value);
      return option ? [option.id] : [];
    });
    if (selectedIds.length !== includedAssetIds.length || selectedIds.some((id) => !includedAssetIds.includes(id))) {
      onChange(selectedIds);
    }
  };

  return (
    <SelectField
      multiple
      fullWidth
      sections={[
        {
          options: [
            { label: selectAllLabel, value: selectAllValue, disabled: selectableIds.length === 0 },
            ...(showAggregate ? [{ label: aggregateLabel, value: aggregateValue }] : []),
          ],
        },
        { options },
      ]}
      value={[
        ...(allSelected ? [selectAllValue] : []),
        ...(showAggregate && aggregate ? [aggregateValue] : []),
        ...selectedValues,
      ]}
      onChange={handleChange}
    />
  );
}
